<script setup>
import CaretDownIcon from '@iconify-vue/ph/caret-down';
import { AnimatePresence, Motion } from 'motion-v';
import { CollapsibleRoot, CollapsibleTrigger } from 'reka-ui';
import { Comment, computed, Fragment, ref, Text, useSlots } from 'vue';

const {
    icon,
    title,
    withoutPadding = false,
    withoutBorder = false,
    defaultOpen = true,
    keepMounted = false,
} = defineProps({
    icon: {
        required: false,
        type: Object,
    },
    title: {
        required: false,
        type: String,
    },
    withoutPadding: {
        required: false,
        type: Boolean,
    },
    withoutBorder: {
        required: false,
        type: Boolean,
    },
    defaultOpen: {
        required: false,
        type: Boolean,
    },
    keepMounted: {
        required: false,
        type: Boolean,
    },
});

const isOpen = ref(defaultOpen);

const slots = useSlots();

function hasSlotContent(slot) {
    if (!slot) {
        return false;
    }

    return slot().some((vnode) => {
        if (vnode.type === Comment) {
            return false;
        }

        if (vnode.type === Fragment) {
            return Array.isArray(vnode.children) && vnode.children.length > 0;
        }

        if (vnode.type === Text) {
            return (
                typeof vnode.children === 'string' &&
                vnode.children.trim() !== ''
            );
        }

        return true;
    });
}

const hasContent = computed(() => hasSlotContent(slots.default));

const hasTitleSlot = computed(() => hasSlotContent(slots.title));

const hasTitle = computed(() => Boolean(title) || hasTitleSlot.value);
</script>

<template>
    <slot v-if="!hasTitle" />

    <div v-else-if="!hasContent">
        <div
            class="flex w-full items-center gap-2 rounded-sm border border-gray-200 bg-indigo-100 px-4 py-3 text-xl font-bold text-indigo-600"
        >
            <slot v-if="hasTitleSlot" name="title" />

            <div v-else class="truncate">
                {{ title }}
            </div>
        </div>
    </div>

    <CollapsibleRoot v-else v-model:open="isOpen">
        <CollapsibleTrigger
            class="flex w-full items-center gap-2 rounded-t-sm border border-gray-200 bg-indigo-100 px-4 py-3 text-xl font-bold text-indigo-600 duration-150 hover:bg-indigo-200 focus:outline-hidden"
            :class="{
                'rounded-b-sm': !isOpen,
                'border-b-0': isOpen,
            }"
        >
            <component v-if="icon" :is="icon" class="size-5 shrink-0" />

            <slot v-if="hasTitleSlot" name="title" />

            <div v-else class="truncate">
                {{ title }}
            </div>

            <CaretDownIcon
                class="ml-auto size-4 shrink-0 duration-150"
                :class="{ 'rotate-180 transform': isOpen }"
            />
        </CollapsibleTrigger>

        <Motion
            v-if="keepMounted"
            :initial="false"
            :animate="
                isOpen
                    ? { height: 'auto', opacity: 1 }
                    : { height: 0, opacity: 0 }
            "
            :transition="{ duration: 0.2 }"
            class="overflow-hidden"
        >
            <div
                class="rounded-b-sm bg-white"
                :class="{
                    'px-4 py-3': !withoutPadding,
                    'border border-gray-200': !withoutBorder,
                }"
            >
                <slot />
            </div>
        </Motion>

        <AnimatePresence v-else :initial="false">
            <Motion
                v-if="isOpen"
                :initial="{ height: 0, opacity: 0 }"
                :animate="{ height: 'auto', opacity: 1 }"
                :exit="{ height: 0, opacity: 0 }"
                :transition="{ duration: 0.2 }"
                class="overflow-hidden"
            >
                <div
                    class="rounded-b-sm bg-white"
                    :class="{
                        'px-4 py-3': !withoutPadding,
                        'border border-gray-200': !withoutBorder,
                    }"
                >
                    <slot />
                </div>
            </Motion>
        </AnimatePresence>
    </CollapsibleRoot>
</template>
